import { matchPath } from "react-router";
import { useTranslation } from "react-i18next";
import { getCourse } from "@nualang/nualang-api-and-queries/APIs/Courses";
import { createClassroom as createClassroomAPI } from "@nualang/nualang-api-and-queries/APIs/Classrooms";
import { classrooms as classroomQuerys } from "@nualang/nualang-api-and-queries/Queries";
import i18n from "../i18n";
import { searchStringToObj } from "../utils";

const useCreateFirstClassroom = ({
  location,
  navigate,
  attributes,
  freeTrial = false,
}) => {
  const { t } = useTranslation();

  const searchParams = searchStringToObj(location.search);
  const { redirect } = searchParams;

  const createClassroomMutation =
    classroomQuerys.useCreateClassroom(createClassroomAPI);

  const getForLang = () => {
    if (i18n.language === "en") {
      return "english";
    } else if (i18n.language === "en-US") {
      return "english";
    } else if (i18n.language === "it") {
      return "italian";
    } else if (i18n.language === "de") {
      return "german";
    } else if (i18n.language === "ga") {
      return "irish";
    } else if (i18n.language === "pt") {
      return "portuguese";
    } else if (i18n.language === "fr") {
      return "french";
    } else if (i18n.language === "es") {
      return "spanish";
    }
  };

  const createFirstClassroom = async () => {
    const enrollmentKey = `${attributes[`custom:learnLang`]}${Math.floor(Math.random() * 999 + 100)}`;
    const forLang = getForLang();

    let values = {
      classroomName: `${t(attributes[`custom:learnLang`])} ${t("classroom")} | ${attributes[`preferred_username`]}`,
      description: `${t("first_classroom_description", { learnLang: t(attributes[`custom:learnLang`]) })}`,
      learnLang: attributes[`custom:learnLang`],
      forLang: forLang,
      visibility: "private",
      vchatEnabled: "false",
      vchatEnabledInSettings: "false",
      enrolmentKey: enrollmentKey,
      allowedDomains: [],
      collaborators: [],
      tags: [],
    };

    const match = redirect
      ? matchPath(
          {
            path: "/courses/:courseId",
            exact: true,
            strict: false,
          },
          new URL(redirect).pathname,
        ) ||
        matchPath(
          {
            path: "/courses/:courseId/:sectionId",
            exact: true,
            strict: false,
          },
          new URL(redirect).pathname,
        ) ||
        matchPath(
          {
            path: "/courses/:courseId/:sectionId/:topicId",
            exact: true,
            strict: false,
          },
          new URL(redirect).pathname,
        )
      : null;

    const isCourseId =
      match !== null &&
      match.params.courseId !== "search" &&
      match.params.courseId !== "create";

    if (isCourseId) {
      const courseId = match.params.courseId;
      const course = await getCourse(courseId);
      if (course.Item.visibility === "public") {
        values = {
          ...values,
          courses: [`${courseId}`],
        };
      }
    }

    return createClassroomMutation.mutate(
      { values },
      {
        onSettled: () => {
          // Error or success... doesn't matter!
        },
        onSuccess: async (data) => {
          const newClassroom = data.Item;
          const redirectUrl = redirect ? new URL(redirect) : null;
          let continueChecks = 1;
          if (redirectUrl && redirectUrl.pathname.includes("classrooms/")) {
            const redirectUrlSearchParams = searchStringToObj(
              redirectUrl.search,
            );
            if (
              redirectUrlSearchParams.plan &&
              redirectUrlSearchParams.learnlang
            ) {
              continueChecks = 0;
              navigate("/classrooms");
            } else {
              continueChecks = 0;
              navigate(`/classrooms`);
            }
          } else if (
            continueChecks === 1 &&
            redirectUrl &&
            redirectUrl.pathname !== "/classrooms" &&
            !redirectUrl.pathname.includes("courses")
          ) {
            continueChecks = 0;
            window.location = redirect;
          } else if (continueChecks === 1) {
            navigate(
              `/classrooms/${newClassroom.classroomId}${freeTrial ? `?freeTrial=true` : ``}`,
            );
          } else {
            navigate(`/classrooms`);
          }
        },
        onError: (error) => {
          logger.error(error);
          navigate(`/classrooms`);
        },
      },
    );
  };

  return {
    createFirstClassroom,
  };
};
export default useCreateFirstClassroom;
